<script setup lang="ts">
/**
 * The admin panel, inside the game.
 *
 * ── Why it is here at all ────────────────────────────────────────────────────
 *
 * It replaces Kottster, which was a second React application in its own
 * container with its own login, its own session, its own design system and its
 * own database connection. Two of its thirteen pages proxied to this backend's
 * admin API; the other eleven were generic Postgres table browsers.
 *
 * The whole reason it had to be a separate server-side app is that
 * `AdminAccessGuard` only accepted a shared secret or an API key, and a browser
 * cannot hold either without embedding it in client code. That guard now
 * accepts an ADMIN's own session cookie, so the panel can be a screen in the
 * game — one login, one design, one deployment, one set of API calls, and no
 * second container to build, run and keep patched.
 *
 * ── What was lost, deliberately ──────────────────────────────────────────────
 *
 * Eight of Kottster's table browsers had no REST endpoint behind them —
 * payments, currency ledger, quests, user quests, seasons, season rewards, chat
 * messages, matches, inventory. They are not here, and no replacement was
 * built. Browsing those now means `psql` or new endpoints. See
 * `admin-api.ts`'s header; the gap is written down in two places on purpose,
 * because an absent page is otherwise indistinguishable from a broken one.
 *
 * ── The role check is a *convenience*, not the security boundary ─────────────
 *
 * This screen renders when `session.player.isAdmin` is true. That is a hint
 * from the browser and is treated as one: every request it makes is checked
 * server-side by `AdminAccessGuard`, which re-reads the account's role from the
 * database on every call rather than trusting a fifteen-minute-old token. A
 * tampered `isAdmin` produces a panel where every panel fails with 403 — which
 * is what the non-admin branch below says out loud rather than pretending the
 * URL does not exist.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import BrandLogo from '@/ui/BrandLogo.vue';
import { useSession } from '@/state/session';
import {
  ADMIN_SECTIONS,
  ADMIN_SECTION_LABELS,
  leaveAdmin,
  navigateAdmin,
  type AdminSection,
} from '@/state/admin-route';

import OverviewPane from './OverviewPane.vue';
import PlayersPane from './PlayersPane.vue';
import ContentPane from './ContentPane.vue';
import InvoicesPane from './InvoicesPane.vue';
import SettingsPane from './SettingsPane.vue';
import PaymentMethodsPane from './PaymentMethodsPane.vue';
import MetaPane from './MetaPane.vue';
import KeysPane from './KeysPane.vue';
import WithdrawalsPane from './WithdrawalsPane.vue';

const props = defineProps<{ section: AdminSection }>();

const session = useSession();
const isAdmin = computed(() => session.player.value?.isAdmin === true);

/**
 * The panel is a full-screen surface, so the body must not keep the game's own
 * scroll behind it. Restored on unmount, not set-and-forget: the player goes
 * back to a garage that expects its own overflow rules.
 */
let previousOverflow = '';
onMounted(() => {
  previousOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
});
onBeforeUnmount(() => {
  document.body.style.overflow = previousOverflow;
});

/** Which pane component a section maps to. One table, so the two cannot drift. */
const PANES = {
  overview: OverviewPane,
  players: PlayersPane,
  content: ContentPane,
  invoices: InvoicesPane,
  settings: SettingsPane,
  'payment-methods': PaymentMethodsPane,
  withdrawals: WithdrawalsPane,
  meta: MetaPane,
  keys: KeysPane,
} as const;

const pane = computed(() => PANES[props.section]);

/** Narrow screens collapse the rail to a select; see the stylesheet. */
const railOpen = ref(false);
watch(() => props.section, () => (railOpen.value = false));
</script>

<template>
  <div class="admin" data-test="admin-screen">
    <header class="admin-bar">
      <button
        type="button"
        class="admin-burger"
        aria-label="Sections"
        data-test="admin-burger"
        @click="railOpen = !railOpen"
      >
        <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <path
            d="M2 4h12M2 8h12M2 12h12"
            fill="none"
            stroke="currentColor"
            stroke-width="1.4"
            stroke-linecap="round"
          />
        </svg>
      </button>

      <BrandLogo class="admin-logo" />
      <span class="admin-kicker">Admin</span>

      <div class="admin-spacer"></div>

      <span v-if="session.player.value" class="admin-who" data-test="admin-who">
        {{ session.player.value.nickname }}
      </span>
      <button type="button" class="ghost" data-test="admin-leave" @click="leaveAdmin()">
        Back to game
      </button>
    </header>

    <!--
      Not a 404 and not a redirect. Somebody who reaches this URL without the
      role has either mistyped it or been given it by mistake, and both are
      better served by a sentence than by the app pretending the page is not
      there — which is also a lie, since the page exists and the *API* is what
      refuses them.
    -->
    <div v-if="!isAdmin" class="admin-denied" data-test="admin-denied">
      <h1>Administrators only</h1>
      <p>
        This account does not have access to the admin panel. If that is wrong,
        ask an operator to change your role.
      </p>
      <button type="button" class="primary" @click="leaveAdmin()">Back to the game</button>
    </div>

    <div v-else class="admin-body" :class="{ 'rail-open': railOpen }">
      <nav class="admin-rail" aria-label="Admin sections">
        <button
          v-for="s in ADMIN_SECTIONS"
          :key="s"
          type="button"
          class="admin-rail-item"
          :class="{ active: s === section }"
          :aria-current="s === section ? 'page' : undefined"
          :data-test="`admin-nav-${s}`"
          @click="navigateAdmin(s)"
        >
          {{ ADMIN_SECTION_LABELS[s] }}
        </button>
      </nav>

      <main class="admin-main">
        <component :is="pane" />
      </main>
    </div>
  </div>
</template>

<style scoped>
.admin {
  position: fixed;
  inset: 0;
  z-index: 90;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  background: var(--steel-900);
  color: var(--steel-200);
  font-family: var(--font-body);
}

.admin-bar {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.5rem 0.9rem;
  border-bottom: 1px solid var(--steel-600);
  background: var(--steel-800);
}

.admin-logo {
  --brand-logo-height: 1.05rem;
  opacity: 0.9;
}

.admin-kicker {
  padding: 0.1rem 0.4rem;
  border: 1px solid var(--accent-dim);
  border-radius: 0.15rem;
  color: var(--accent);
  font-family: var(--font-mono);
  font-size: 0.6rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.admin-spacer { flex: 1; }

.admin-who {
  color: var(--steel-400);
  font-size: 0.78rem;
}

/* Hidden until the rail collapses — see the narrow-width block. */
.admin-burger {
  display: none;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 1px solid var(--steel-600);
  border-radius: 0.2rem;
  background: var(--steel-700);
  color: var(--steel-200);
  cursor: pointer;
}
.admin-burger svg { width: 1rem; height: 1rem; }

.admin-body {
  display: grid;
  grid-template-columns: 12.5rem minmax(0, 1fr);
  min-height: 0;
}

.admin-rail {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  padding: 0.6rem 0.4rem;
  border-right: 1px solid var(--steel-600);
  background: var(--steel-800);
  overflow-y: auto;
}

.admin-rail-item {
  padding: 0.5rem 0.6rem;
  /* The 44px tap floor the mobile pass set, minus the border. */
  min-height: 2.4rem;
  border: none;
  border-left: 2px solid transparent;
  border-radius: 0.15rem;
  background: none;
  color: var(--steel-400);
  font: inherit;
  font-size: 0.82rem;
  text-align: left;
  cursor: pointer;
}

.admin-rail-item:hover {
  background: var(--steel-700);
  color: var(--steel-200);
}

/* A left rule rather than a filled block: at this size a fill reads as a
   selected row in a table rather than as "you are here". */
.admin-rail-item.active {
  border-left-color: var(--accent);
  background: color-mix(in srgb, var(--accent) 10%, transparent);
  color: #fff;
}

.admin-main {
  min-height: 0;
  padding: 1rem 1.2rem 3rem;
  overflow-y: auto;
}

.admin-denied {
  display: grid;
  align-content: center;
  justify-items: start;
  gap: 0.7rem;
  max-width: 32rem;
  margin: 0 auto;
  padding: 2rem 1.2rem;
}
.admin-denied h1 { margin: 0; font-family: var(--font-display); font-size: 1.5rem; }
.admin-denied p { margin: 0; color: var(--steel-400); }

.ghost, .primary {
  min-height: 2.1rem;
  padding: 0 0.7rem;
  border: 1px solid var(--steel-600);
  border-radius: 0.2rem;
  background: var(--steel-700);
  color: var(--steel-200);
  font: inherit;
  font-size: 0.78rem;
  cursor: pointer;
}
.primary { border-color: var(--accent-dim); background: var(--accent); color: var(--steel-900); font-weight: 600; }
.ghost:hover { border-color: var(--accent-dim); color: #fff; }

/*
 * Below 40rem the rail becomes a drawer over the content rather than a column
 * beside it. A 12.5rem rail on a 20rem screen leaves seven characters for a
 * table.
 */
@media (max-width: 40rem) {
  .admin-burger { display: grid; place-items: center; }
  .admin-body { grid-template-columns: minmax(0, 1fr); }
  .admin-rail {
    position: absolute;
    top: 3rem;
    bottom: 0;
    left: 0;
    z-index: 2;
    width: 12.5rem;
    transform: translateX(-100%);
    transition: transform 140ms ease;
  }
  .admin-body.rail-open .admin-rail { transform: none; }
}
</style>
